import { useAuth } from '../hooks/useAuth'
import { useAtomValue, useSetAtom } from 'jotai'
import { accessTokenAtom, setUserAtom, logoutAtom } from '../store/userAtoms'
import { setupAxiosInterceptor } from '../modules/apiClients'
import { useSessionKeepAlive } from '../hooks/useSessionKeepAlive'
import { useCallback, useEffect, useRef } from 'react'
import { StandardLoader } from './StandardLoader'
import { isPrerenderedPath } from '@/modules/seo/prerenderedPaths'
import { useTar } from '@/modules/i18n'
import { toast } from 'sonner'

interface AuthInitializerProps {
  children: React.ReactNode
}

export const AuthInitializer: React.FC<AuthInitializerProps> = ({ children }) => {
  const { initializeAuth, isInitializing, clearError } = useAuth()
  const { t } = useTar()
  const accessToken = useAtomValue(accessTokenAtom)
  const setUser = useSetAtom(setUserAtom)
  const setAccessTokenAtom = useSetAtom(accessTokenAtom)
  const logout = useSetAtom(logoutAtom)
  const accessTokenRef = useRef<string | null>(accessToken)
  // Frozen at mount: if the initial URL was a prerendered path, the HTML already
  // shows real content, so skip the full-screen loader to avoid a flash.
  const skipLoaderRef = useRef(isPrerenderedPath(window.location.pathname))

  useEffect(() => {
    accessTokenRef.current = accessToken
  }, [accessToken])

  const updateAccessToken = useCallback(
    (token: string | null) => {
      accessTokenRef.current = token
      setAccessTokenAtom(token)
    },
    [setAccessTokenAtom]
  )

  const handleLogout = useCallback(() => {
    accessTokenRef.current = null
    logout()
  }, [logout])

  // Shown when a refresh fails (the session truly expired — e.g. a tab left open past
  // the refresh-cookie window). The logout itself is already done by refreshAccessToken;
  // this just tells the user. Persistent with a dismiss button; a fixed id dedupes
  // repeats so lingering requests can't stack multiple toasts.
  const handleSessionExpired = useCallback(() => {
    toast.error(t('errors.sessionExpired'), {
      id: 'session-expired',
      description: t('errors.sessionExpiredDescription'),
      duration: Infinity,
      action: {
        label: t('common.close'),
        onClick: () => { /* dismiss handled by sonner */ }
      }
    })
  }, [t])

  // Set up axios interceptors with automatic session refresh capability 🏴‍☠️⭐
  useEffect(() => {
    const cleanupInterceptors = setupAxiosInterceptor(
      () => accessTokenRef.current,     // Get current access token
      updateAccessToken,        // Update access token after refresh
      setUser,              // Update user data after refresh
      handleLogout,               // Logout if refresh fails
      clearError,           // Clear any lingering auth errors after refresh
      handleSessionExpired        // Notify the user when the session expires
    )
    return cleanupInterceptors
  }, [updateAccessToken, setUser, handleLogout, clearError, handleSessionExpired])

  // Run once on mount only. Using [initializeAuth] causes infinite retries: when refresh
  // fails, logout() clears accessToken → initializeAuth gets new ref → effect re-runs.
  useEffect(() => {
    initializeAuth()
  }, [])

  // Keep an actively-used session's token fresh on activity/focus (no background timer),
  // so requests never carry an expired token (which would 401 and flood Sentry), while
  // idle/blurred tabs still lapse and log out once the refresh cookie expires.
  useSessionKeepAlive()

  // Show loading state while initializing auth
  if (isInitializing && !skipLoaderRef.current) {
    return <StandardLoader text="Initializing..." fullScreen />
  }
  return <>{children}</>
}
